import React, { useEffect, useState } from 'react';
import Sidebar from '../components/layout/sidebar';
import Topbar from '../components/layout/Topbar';
import { supabase } from '../supabase';
import './landingContent.css';

const defaultContent = {
  hero: {
    title_en: 'Step beyond the page',
    content_en:
      'Discover curated literary events — from intimate book clubs to grand festivals.',
    image_url: '',
  },
  about: {
    title_en: 'About Spine',
    content_en:
      'Spine is your gateway to the vibrant world of literary events.',
    image_url: '',
  },
  featured: {
    title_en: 'Literary Festival 2026',
    content_en:
      'Join us for three days of literary celebration featuring authors, panels, workshops, and book signings.',
    image_url: '',
  },
  testimonials: {
    title_en: 'Testimonials',
    content_en: '',
    image_url: '',
  },
  newsletter: {
    title_en: 'Stay Updated',
    content_en: 'Get weekly literary events delivered to your inbox.',
    image_url: '',
  },
  footer: {
    title_en: 'Footer Content',
    content_en: '© 2026 Spine Literary Events. All rights reserved.',
    image_url: '',
  },
};

const LandingContent = () => {
  const [content, setContent] = useState(defaultContent);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (localStorage.getItem('isAdminLoggedIn') !== 'true') {
      window.location.href = '/';
      return;
    }

    fetchContent();
  }, []);

  const fetchContent = async () => {
    setLoading(true);

    const { data, error } = await supabase.from('site_content').select('*');

    if (error) {
      alert(error.message);
      setLoading(false);
      return;
    }

    const loaded = { ...defaultContent };

    data?.forEach((item) => {
      loaded[item.section_key] = {
        title_en: item.title_en || '',
        title_ar: item.title_ar || '',
        content_en: item.content_en || '',
        content_ar: item.content_ar || '',
        image_url: item.image_url || '',
        is_active: item.is_active,
      };
    });

    setContent(loaded);
    setLoading(false);
  };

  const handleChange = (section, field, value) => {
    setContent({
      ...content,
      [section]: {
        ...content[section],
        [field]: value,
      },
    });
  };

  const saveSection = async (sectionKey) => {
    const section = content[sectionKey];

    const payload = {
      section_key: sectionKey,
      title_en: section.title_en,
      title_ar: section.title_ar || '',
      content_en: section.content_en,
      content_ar: section.content_ar || '',
      image_url: section.image_url || '',
      is_active: true,
    };

    const { error } = await supabase
      .from('site_content')
      .upsert(payload, { onConflict: 'section_key' });

    if (error) {
      alert(error.message);
      return false;
    }

    return true;
  };

  const saveAllContent = async () => {
    setSaving(true);

    for (const key of Object.keys(content)) {
      const success = await saveSection(key);
      if (!success) {
        setSaving(false);
        return;
      }
    }

    setSaving(false);
    alert('Landing page content saved successfully');
    fetchContent();
  };

  return (
    <div className="dashboard-page">
      <Sidebar active="Landing Page Content" />

      <main className="dashboard-main">
        <Topbar />

        <section className="landing-content-page">
          <div className="landing-content-header">
            <div>
              <h1>Landing Page Content</h1>
              <p>Manage homepage content and sections</p>
            </div>

            <button className="save-content-btn" onClick={saveAllContent}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>

          {loading ? (
            <p className="content-loading">Loading content...</p>
          ) : (
            <>
              <ContentCard
                icon="▣"
                title="Hero Banner"
                section="hero"
                content={content}
                handleChange={handleChange}
                fields={[
                  ['title_en', 'Title'],
                  ['content_en', 'Subtitle', 'textarea'],
                  ['image_url', 'Background Image URL'],
                ]}
              />

              <ContentCard
                icon="T"
                title="About Section"
                section="about"
                content={content}
                handleChange={handleChange}
                fields={[
                  ['title_en', 'Title'],
                  ['content_en', 'Description', 'textarea'],
                  ['image_url', 'Image URL'],
                ]}
              />

              <ContentCard
                icon="▧"
                title="Featured Event"
                section="featured"
                content={content}
                handleChange={handleChange}
                fields={[
                  ['title_en', 'Event Name'],
                  ['content_en', 'Description', 'textarea'],
                  ['image_url', 'Image URL'],
                ]}
              />

              <div className="content-card">
                <h2><span>♡</span> Testimonials</h2>

                <div className="testimonial-box">
                  <label>Testimonial 1 Name</label>
                  <input
                    value={content.testimonials.title_en}
                    onChange={(e) =>
                      handleChange('testimonials', 'title_en', e.target.value)
                    }
                    placeholder="Sarah Johnson"
                  />

                  <label>Quote</label>
                  <textarea
                    value={content.testimonials.content_en}
                    onChange={(e) =>
                      handleChange('testimonials', 'content_en', e.target.value)
                    }
                    placeholder="Quote"
                  />
                </div>

                <div className="testimonial-box">
                  <label>Testimonial 2 Name</label>
                  <input
                    value={content.testimonials.title_ar || ''}
                    onChange={(e) =>
                      handleChange('testimonials', 'title_ar', e.target.value)
                    }
                    placeholder="Michael Chen"
                  />

                  <label>Quote</label>
                  <textarea
                    value={content.testimonials.content_ar || ''}
                    onChange={(e) =>
                      handleChange('testimonials', 'content_ar', e.target.value)
                    }
                    placeholder="Quote"
                  />
                </div>
              </div>

              <ContentCard
                icon="✉"
                title="Newsletter Section"
                section="newsletter"
                content={content}
                handleChange={handleChange}
                fields={[
                  ['title_en', 'Title'],
                  ['content_en', 'Description', 'textarea'],
                  ['image_url', 'Input Placeholder'],
                ]}
              />

              <ContentCard
                icon=""
                title="Footer Content"
                section="footer"
                content={content}
                handleChange={handleChange}
                fields={[
                  ['content_en', 'Description', 'textarea'],
                  ['title_en', 'Copyright Text'],
                ]}
              />
            </>
          )}
        </section>

        <footer className="dashboard-footer">
          <p>© 2026 Spine Literary Events Platform. All rights reserved.</p>
          <p>Admin Dashboard v1.0</p>
        </footer>
      </main>
    </div>
  );
};

const ContentCard = ({ icon, title, section, content, handleChange, fields }) => {
  return (
    <div className="content-card">
      <h2>
        <span>{icon}</span> {title}
      </h2>

      {fields.map(([field, label, type]) => (
        <div className="content-field" key={field}>
          <label>{label}</label>

          {type === 'textarea' ? (
            <textarea
              value={content[section]?.[field] || ''}
              onChange={(e) => handleChange(section, field, e.target.value)}
            />
          ) : (
            <input
              value={content[section]?.[field] || ''}
              onChange={(e) => handleChange(section, field, e.target.value)}
            />
          )}
        </div>
      ))}
    </div>
  );
};

export default LandingContent;